/* RESPONSIBLE TEAM: team-inbox */
import BaseSettingsRoute from 'embercom/routes/apps/app/settings/base/base-settings-route';
import { inject as service } from '@ember/service';
import type Store from '@ember-data/store';
import { action } from '@ember/object';
import { hash } from 'rsvp';
import { taskFor } from 'ember-concurrency-ts';
import type Session from 'embercom/services/session';
import type IntlService from 'ember-intl/services/intl';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import type Transition from '@ember/routing/transition';
import type AppService from 'embercom/services/app-service';

export default class ConversationTicketsRoute extends BaseSettingsRoute {
  @service declare appService: AppService;
  @service declare permissionsService: $TSFixMe;
  @service declare store: Store;
  @service declare session: Session;
  @service declare intl: IntlService;
  @service declare finStandaloneService: FinStandaloneService;

  get titleToken() {
    return this.intl.t('new-settings.submenu.data.conversation-tickets');
  }

  async beforeModel(transition: Transition) {
    await super.beforeModel(transition);

    this.session.switchWorkspace(this.appService.app.id);
    await this.session.workspace.fetchRequiredAttributes();

    let permission = 'can_access_product_settings';
    await this.permissionsService.ensurePermissionWhenTransitioning(permission, transition);

    if (this.appService.app.canUseStandalone) {
      await this.finStandaloneService.initialize();

      let promises: Promise<void>[] = [];

      promises.push(this.appService.mainStandalonePlatform.loadDataFields());

      // TODO: SA Generic platform migration – Remove SFDC logic when data loading moved to the platform object
      if (this.appService.app.hasStandalonePlatform('salesforce')) {
        if (
          taskFor(this.finStandaloneService.salesforceDataLoader.loadSalesforceDataFields).isRunning
        ) {
          promises.push(
            taskFor(this.finStandaloneService.salesforceDataLoader.loadSalesforceDataFields)
              .lastRunning!,
          );
        } else {
          promises.push(
            taskFor(
              this.finStandaloneService.salesforceDataLoader.loadSalesforceDataFields,
            ).perform(),
          );
        }
      }

      // we call load data fields in the finStandaloneService.initialize method
      // but we are not returning them in the returned promise list
      // the intention here is to wait for these promises to resolve before
      // we navigate to this route
      await Promise.all(promises);
    }
  }

  async model() {
    return hash({
      reloadAttributeDescriptors: this.reloadAttributeDescriptors,
      descriptors: this.store.query('conversation-attributes/descriptor', {
        system_defined: false,
      }),
    });
  }

  @action
  reloadAttributeDescriptors() {
    this.refresh();
  }
}
